const feature = {
  slug: "instagram-product-catalog-checkout",
  path: "/features/instagram-product-catalog-checkout",
  eyebrow: "Instagram product catalog and checkout",
  h1: "An Instagram product catalog and checkout that live inside the chat",
  seoTitle: "Instagram Product Catalog and Checkout | Checkout DM",
  description:
    "Customers browse your catalog, pick a size, enter delivery details and pay inside Instagram DMs. See how products, stock, payment and shipping work.",
  intro:
    "Selling through DMs usually means answering “what do you have?”, typing prices and chasing addresses. Checkout DM puts the catalog and the checkout into the conversation, so the customer can browse, choose, enter details and pay without leaving Instagram.",
  glance: [
    { label: "Catalog size", value: "5 on Free, up to 10,000 on Pro" },
    { label: "Payments", value: "UPI, QR, bank, Razorpay, Stripe, PayPal" },
    { label: "Commission", value: "None on any plan" },
  ],
  blocks: [
    { type: "h2", text: "Your catalog, shown in the chat" },
    {
      type: "p",
      text:
        "You add products once, with photos, names, prices and sizes. Customers then browse them inside the DM, and the bot shows the photo, name, price and sizes of each product exactly as you entered them. Customers can also search the catalog by typing what they want, such as “men black slim jeans under 2000”.",
    },
    {
      type: "ul",
      items: [
        "**Sizes and stock per size.** If a size sells out, it is not offered again, and a product that reaches zero is hidden automatically when you track quantity.",
        "**Catalog limits by plan.** Free allows 5 products and Starter 500. Growth allows 1,000 and Pro 10,000. The Creator plan allows up to 150 digital products.",
        "**Digital products.** After payment, a digital product is delivered to the customer by link.",
      ],
    },

    { type: "h2", text: "From choice to checkout" },
    {
      type: "p",
      text:
        "Once the customer picks a product, the bot collects the details it needs one step at a time: size and quantity, then name, phone number, email address (required on paid orders) and the delivery address by country, state, city, PIN code and street. A returning customer can reuse the details saved from an earlier order. Our guide to [writing product descriptions that sell](/blog/write-product-descriptions-instagram-sellers) and [catalog tips](/blog/instagram-catalog-tips-photos-sizes) help you set up products that convert.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "Checkout in the chat",
      caption: "Illustrative example. Your own messages use your products and wording.",
      messages: [
        { from: "shop", text: "Added to your cart: Linen Kurta Set, M, 1 · ₹1,899. Add anything else?" },
        { from: "buyer", text: "No, checkout" },
        { from: "shop", text: "What name should we put on the order?" },
        { from: "buyer", text: "Riya Sharma" },
        { from: "shop", text: "Thanks, Riya. What is your phone number?" },
        { from: "time", text: "Address and payment follow in the same thread" },
      ],
    },

    { type: "h2", text: "Payment, shipping and discounts" },
    {
      type: "ul",
      items: [
        "**Payment on every plan.** Take UPI, QR code or bank transfer with manual confirmation, or connect Razorpay, Stripe or PayPal for automatic confirmation. One automatic gateway can be active at a time.",
        "**Shipping fee at checkout.** A fee is added on top of the product price and paid by the customer. From Starter, you can also make shipping free automatically above a minimum cart value you set.",
        "**Coupons and offers on Growth and Pro.** Automatic and manual coupons, time-based and count-based offers, and on Pro, quantity discounts such as Buy 2 Get 10% off.",
      ],
    },
    {
      type: "p",
      text:
        "Our guide to [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) explains which payment method suits you, and [what Checkout DM really costs](/blog/what-checkout-dm-really-costs-plan-gateway-fees) lists the fees outside the plan.",
    },

    { type: "h2", text: "A Storefront link for sellers who want one" },
    {
      type: "p",
      text:
        "From the Creator plan, you get a shareable Storefront link, in the form checkoutdm.com/store/yourhandle. Customers can browse your catalog, add items to a cart and pay on the page without sending you a DM first. You can share it in your bio, on WhatsApp, Telegram, YouTube, Facebook, a website or any chat app, and buyers do not need an Instagram account to order from it. Starter adds richer product pages with an extended description, sizing guide, care instructions and FAQs, and Pro adds product pages that search engines can index.",
    },

    { type: "h2", text: "Returns and exchanges from the same chat" },
    {
      type: "p",
      text:
        "A customer who wants to return or exchange something can start the request in the DM. Your return and exchange policy and FAQs are shown automatically. For exchanges, Checkout DM checks that the new size is in stock before approving it.",
    },

    { type: "h2", text: "What it does not do" },
    {
      type: "ul",
      items: [
        "**It is not a full website builder.** Your Storefront lives at a checkoutdm.com link rather than on a site you design yourself.",
        "**It does not ship your parcels.** You ship orders, and courier connections such as Shiprocket and Shippo update delivery status.",
        "**It does not take a cut.** The flat plan price is what you pay Checkout DM. Your own payment gateway still charges its own fee if you use one.",
      ],
    },
  ],
  related: [
    { to: "/features/instagram-order-management", title: "Order management", description: "Orders, returns and shipping in one dashboard." },
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "The full chat flow from first message to order." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Plans, product limits and features side by side." },
    { to: "/features/instagram-comment-to-dm-automation", title: "Comment-to-DM automation", description: "Start the checkout from a comment on your Reel." },
  ],
  faqs: [
    {
      q: "How does checkout work inside Instagram DMs?",
      a: "The customer picks a product from your catalog in the chat, chooses a size and quantity, and enters their name, phone number and delivery address one step at a time. They then pay through a payment link or manual payment details sent in the same chat.",
    },
    {
      q: "Which payment methods can I accept?",
      a: "UPI ID, QR code, bank transfer, Razorpay, Stripe and PayPal, on every plan at no extra cost. Manual payments need your confirmation, while gateway payments are confirmed automatically.",
    },
    {
      q: "Does Checkout DM charge a commission on my sales?",
      a: "No. There is no commission or transaction fee on any plan. Payments go to your own account, and a payment gateway charges its own fee if you use one.",
    },
    {
      q: "How many products can I list?",
      a: "Free allows 5, Creator up to 150 digital products, Starter 500, Growth 1,000 and Pro 10,000.",
    },
    {
      q: "Can I sell digital products?",
      a: "Yes. A digital product is delivered by link after payment on every plan, and the Creator plan is built for digital-only sellers.",
    },
  ],
};

export default feature;
